// Deterministic projection of Francis's rate-card Sell build-up into the
// client-facing QuoteMaker line amounts. No AI is allowed in this boundary.
//
// Pricing-core (Quote Brain DESIGN.md 4.2): the rate card whose methodology
// is the base layer is an argument (resolvePricingMethodology). RJ Bird's
// binding is rj-bird-methodology.js.

import { STATUTORY_INSURANCE_RE } from './cost-rules.js';

const round2 = (value) => Math.round((Number(value) || 0) * 100) / 100;
const methodField = (object, snakeKey, camelKey) => object?.[snakeKey] ?? object?.[camelKey];

const isPlainRecord = (value) => !!value && typeof value === 'object' && !Array.isArray(value);

function applyExplicitAliasOverrides(target, configured, aliasPairs) {
    for (const [snakeKey, camelKey] of aliasPairs) {
        const hasSnake = Object.prototype.hasOwnProperty.call(configured, snakeKey);
        const hasCamel = Object.prototype.hasOwnProperty.call(configured, camelKey);
        if (!hasSnake && !hasCamel) continue;
        // Methodology readers prefer snake_case. Mirror an explicit tenant value
        // onto both spellings so the embedded snake_case default cannot shadow a
        // camelCase override from a persisted Pricing snapshot.
        const value = hasSnake ? configured[snakeKey] : configured[camelKey];
        target[snakeKey] = value;
        target[camelKey] = value;
    }
    return target;
}

function mergePricingMethodologyLayer(configuredMethodology, embeddedMethodology) {
    const configured = isPlainRecord(configuredMethodology) ? configuredMethodology : {};
    const embedded = isPlainRecord(embeddedMethodology) ? embeddedMethodology : {};
    const merged = applyExplicitAliasOverrides(
        { ...embedded, ...configured },
        configured,
        [
            ['default_band', 'defaultBand'],
            ['administration_fee_ex_gst_tiers', 'administrationFeeExGstTiers'],
            ['gst_rate', 'gstRate'],
            ['line_rounding_inc_gst', 'lineRoundingIncGst'],
            ['minimum_attendance_range_inc_gst', 'minimumAttendanceRangeIncGst'],
            ['operator_overrides', 'operatorOverrides'],
        ],
    );

    const configuredHbcf = isPlainRecord(configured.hbcf) ? configured.hbcf : {};
    const embeddedHbcf = isPlainRecord(embedded.hbcf) ? embedded.hbcf : {};
    merged.hbcf = applyExplicitAliasOverrides(
        { ...embeddedHbcf, ...configuredHbcf },
        configuredHbcf,
        [
            ['threshold_inc_gst', 'thresholdIncGst'],
            ['premium_rate', 'premiumRate'],
            ['application_fee_ex_gst', 'applicationFeeExGst'],
        ],
    );

    const configuredCompliance = isPlainRecord(configured.regulated_compliance)
        ? configured.regulated_compliance
        : (isPlainRecord(configured.regulatedCompliance) ? configured.regulatedCompliance : {});
    const embeddedCompliance = isPlainRecord(embedded.regulated_compliance)
        ? embedded.regulated_compliance
        : (isPlainRecord(embedded.regulatedCompliance) ? embedded.regulatedCompliance : {});
    const mergedCompliance = applyExplicitAliasOverrides(
        { ...embeddedCompliance, ...configuredCompliance },
        configuredCompliance,
        [
            ['engineering_srd_dcd_ex_gst', 'engineeringSrdDcdExGst'],
            ['dbpa_registration_ex_gst', 'dbpaRegistrationExGst'],
            ['fair_trading_contract_ex_gst', 'fairTradingContractExGst'],
        ],
    );
    merged.regulated_compliance = mergedCompliance;
    merged.regulatedCompliance = mergedCompliance;

    const configuredBands = isPlainRecord(configured.bands) ? configured.bands : {};
    const embeddedBands = isPlainRecord(embedded.bands) ? embedded.bands : {};
    merged.bands = applyExplicitAliasOverrides(
        { ...embeddedBands, ...configuredBands },
        configuredBands,
        [['med_high', 'medHigh']],
    );
    return merged;
}

/**
 * Resolve persisted Pricing methodology layers over a rate card's own
 * methodology (`rateCardAuthority.pricing_methodology`). Later layers win, so
 * callers can preserve snake-then-camel outer precedence while null, empty
 * and partial records still receive every deterministic statutory default the
 * card carries.
 */
export function resolvePricingMethodology(rateCardAuthority, ...configuredLayers) {
    return configuredLayers.reduce(
        (resolved, layer) => mergePricingMethodologyLayer(layer, resolved),
        mergePricingMethodologyLayer(null, rateCardAuthority?.pricing_methodology || {}),
    );
}

export function normalisePricingBand(value, methodology = {}) {
    const key = String(value || methodField(methodology, 'default_band', 'defaultBand') || 'medium')
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, '_');
    const aliases = { medhigh: 'med_high', medium_high: 'med_high' };
    const resolved = aliases[key] || key;
    const camelBand = resolved === 'med_high' ? 'medHigh' : resolved;
    const multiplier = Number(methodology?.bands?.[resolved] ?? methodology?.bands?.[camelBand]);
    if (Number.isFinite(multiplier) && multiplier > 0) {
        return { id: resolved, multiplier };
    }
    return { id: 'medium', multiplier: Number(methodology?.bands?.medium) || 1 };
}

export function roundClientAmountIncGst(value, unit = 10) {
    const amount = Number(value);
    const step = Number(unit);
    if (!Number.isFinite(amount) || amount <= 0) return 0;
    if (!Number.isFinite(step) || step <= 0) return round2(amount);
    return Math.round(amount / step) * step;
}

/**
 * Preserve the normal nearest-unit presentation unless it would undercut an
 * exact authoritative build-up. This is intentionally separate from
 * `roundClientAmountIncGst`: ordinary Francis lines retain their established
 * nearest-$10 policy, while an aggregated statutory package (whose exact
 * components remain the Stage 4 floor) may only round to the same amount or
 * upward.
 */
export function roundClientAmountIncGstAtOrAbove(value, unit = 10) {
    const amount = Number(value);
    const step = Number(unit);
    const rounded = roundClientAmountIncGst(amount, step);
    if (!Number.isFinite(amount) || amount <= 0 || rounded + 0.001 >= amount) return rounded;
    if (!Number.isFinite(step) || step <= 0) return round2(amount);
    return round2(Math.ceil((amount - 0.001) / step) * step);
}

/**
 * Francis planning treatment for HBCF. The 8.5% premium is an estimated,
 * already-client-facing disbursement; the separate application/admin service
 * is an ex-GST Sell component and therefore attracts GST once. The threshold
 * is strictly OVER $20,000 inc GST. No fixed-point iteration is performed.
 */
export function projectFrancisHbcf(subtotalIncGst, methodology = {}) {
    const hbcf = methodology?.hbcf || {};
    const subtotal = Math.max(0, Number(subtotalIncGst) || 0);
    const thresholdIncGst = Math.max(0, Number(methodField(hbcf, 'threshold_inc_gst', 'thresholdIncGst')) || 0);
    const premiumRate = Math.max(0, Number(methodField(hbcf, 'premium_rate', 'premiumRate')) || 0);
    const applicationFeeExGst = Math.max(0, Number(methodField(hbcf, 'application_fee_ex_gst', 'applicationFeeExGst')) || 0);
    const gstRate = Math.max(0, Number(methodField(methodology, 'gst_rate', 'gstRate')) || 0);
    const required = subtotal > thresholdIncGst;
    if (!required) {
        return {
            required: false,
            thresholdIncGst,
            premiumRate,
            premiumIncGst: 0,
            applicationFeeExGst: 0,
            applicationFeeIncGst: 0,
            totalClientAdditionIncGst: 0,
            estimated: true,
        };
    }
    const premiumIncGst = round2(subtotal * premiumRate);
    const applicationFeeIncGst = round2(applicationFeeExGst * (1 + gstRate));
    return {
        required: true,
        thresholdIncGst,
        premiumRate,
        premiumBaseIncGst: round2(subtotal),
        premiumIncGst,
        applicationFeeExGst: round2(applicationFeeExGst),
        applicationFeeIncGst,
        totalClientAdditionIncGst: round2(premiumIncGst + applicationFeeIncGst),
        estimated: true,
    };
}

export const FRANCIS_HBCF_SYSTEM_MANAGED = 'francis-hbcf-v1';
export const FRANCIS_HBCF_PREMIUM_ID = 'francis-hbcf-premium';
export const FRANCIS_HBCF_APPLICATION_ID = 'francis-hbcf-application-service';
export const FRANCIS_COMPLIANCE_SYSTEM_MANAGED = 'francis-compliance-v1';
export const FRANCIS_PRELIMINARIES_ID = 'francis-preliminaries-administration';

// Exported so a caller can tell, before reconciling, which of its own rows
// the statutory step would refuse as a collision.
export const HBCF_TEXT_RE = /\b(?:hbcf|home\s+building\s+compensation)\b/i;
export const COMPLIANCE_COLLISION_RE = /\b(?:preliminar(?:y|ies)|hbcf|home\s+building\s+compensation|dbpa|regulated\s+design|srd\b|fair\s+trading\s+(?:home\s+building\s+)?contract)\b/i;

function money(value) {
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
    const parsed = Number(String(value ?? '').replace(/[$,\s]/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
}

export function isManagedHbcfRow(item) {
    return item?.systemManaged === FRANCIS_HBCF_SYSTEM_MANAGED
        || item?.system_managed === FRANCIS_HBCF_SYSTEM_MANAGED
        || item?.id === FRANCIS_HBCF_PREMIUM_ID
        || item?.id === FRANCIS_HBCF_APPLICATION_ID;
}

export function isManagedComplianceRow(item) {
    return item?.systemManaged === FRANCIS_COMPLIANCE_SYSTEM_MANAGED
        || item?.system_managed === FRANCIS_COMPLIANCE_SYSTEM_MANAGED
        || item?.id === FRANCIS_PRELIMINARIES_ID;
}

/**
 * True when a row is QuoteMaker-managed statutory/HBCF administration rather
 * than builder-priced eligible works. Eligibility must never be bootstrapped
 * by adding these rows to an otherwise under-threshold quote.
 */
export function isHbcfEligibilityExcludedItem(item) {
    if (!item || typeof item !== 'object') return false;
    if (item.excludeFromHbcfEligibility === true || item.exclude_from_hbcf_eligibility === true) return true;
    if (isManagedComplianceRow(item) || isManagedHbcfRow(item)) return true;
    const identity = `${item?.id || ''} ${item?.code || ''} ${item?.title || ''}`;
    return HBCF_TEXT_RE.test(identity) || STATUTORY_INSURANCE_RE.test(identity);
}

/**
 * Client-visible inc-GST builder works price used solely for HBCF eligibility.
 * The visible quote amount wins over internal unrounded cost authority because
 * the statutory threshold is evaluated against the price offered to the client.
 */
export function calculateHbcfEligibilityBaseIncGst(scopeItems = [], {
    isGstInclusive = true,
    gstRate = 0.1,
} = {}) {
    const visible = (Array.isArray(scopeItems) ? scopeItems : [])
        .filter((item) => !isHbcfEligibilityExcludedItem(item))
        .reduce((sum, item) => sum + Math.max(0, money(item?.amount)), 0);
    return round2(isGstInclusive ? visible : visible * (1 + Math.max(0, Number(gstRate) || 0)));
}

const BUILDER_AMOUNT_OVERRIDE_REASONS = new Set([
    'builder-manual-amount',
    'stage5-manual-amount',
    'operator-manual-amount',
]);

/**
 * A generated statutory row is still builder-owned commercial content once
 * the operator deliberately edits its amount. `amountLocked` alone is not
 * enough: generated HBCF rows are locked from birth. Require the dedicated
 * amount edit receipt written by Stage 4/5 so an arbitrary payload mutation
 * cannot masquerade as builder authority.
 */
export function hasBuilderStatutoryAmountOverride(item) {
    const reason = String(item?.amountLockReason || item?.amount_lock_reason || '').trim().toLowerCase();
    const amountEditor = String(item?.amountEditedBy || item?.amount_edited_by || '').trim().toLowerCase();
    return (item?.amountLocked === true || item?.amount_locked === true)
        && (amountEditor === 'user'
            || amountEditor === 'builder'
            || BUILDER_AMOUNT_OVERRIDE_REASONS.has(reason));
}

export function managedScopeItemIncludesHbcf(item) {
    if (isManagedHbcfRow(item)) return true;
    if (!isManagedComplianceRow(item)) return false;
    const components = Array.isArray(item?.costBreakdown?.complianceComponents)
        ? item.costBreakdown.complianceComponents
        : (Array.isArray(item?.cost_breakdown?.compliance_components)
            ? item.cost_breakdown.compliance_components
            : []);
    return components.some((component) => [
        'hbcf_application',
        'hbcf_premium_estimate',
        'fair_trading_contract',
    ].includes(String(component?.kind || '').trim().toLowerCase()));
}

function applyBuilderStatutoryAmountOverride(generatedRow, priorRow, gstRate = 0.1) {
    if (!hasBuilderStatutoryAmountOverride(priorRow)) return generatedRow;
    const amountIncGst = money(priorRow?.amount);
    if (!(amountIncGst > 0)) return generatedRow;

    const breakdown = isPlainRecord(generatedRow?.costBreakdown)
        ? generatedRow.costBreakdown
        : {};
    const isExemptPremium = generatedRow?.gstExempt === true
        || String(generatedRow?.taxTreatment || '').trim().toLowerCase() === 'statutory_premium_estimate';
    const priorFinalExGst = money(breakdown.finalExGstAddon);
    const priorFinalIncGst = money(breakdown.finalIncGstAddon);
    const nextFinalExGst = isExemptPremium ? 0 : round2(amountIncGst / (1 + gstRate));
    const nextFinalIncGst = isExemptPremium ? round2(amountIncGst) : 0;
    const adjustment = isExemptPremium
        ? round2(nextFinalIncGst - priorFinalIncGst)
        : round2(nextFinalExGst - priorFinalExGst);
    const adjustmentBasis = isExemptPremium ? 'inc_gst' : 'ex_gst';
    const otherCosts = Array.isArray(breakdown.otherCosts)
        ? breakdown.otherCosts.map((line) => ({ ...line }))
        : [];
    if (Math.abs(adjustment) > 0.009) {
        otherCosts.push({
            description: 'Builder-approved statutory amount adjustment',
            qty: 1,
            unit: 'Per project',
            total: adjustment,
            rateIsFinalChargeOut: true,
            rateTaxBasis: adjustmentBasis,
            sourceRating: 'operator_locked',
            operatorOverride: true,
        });
    }

    return {
        ...generatedRow,
        amount: String(round2(amountIncGst)),
        amountAuthority: 'committed',
        amountLocked: true,
        amountLockReason: priorRow?.amountLockReason || priorRow?.amount_lock_reason || 'builder-manual-amount',
        amountEditedAt: priorRow?.amountEditedAt || priorRow?.amount_edited_at || null,
        amountEditedBy: priorRow?.amountEditedBy || priorRow?.amount_edited_by || 'user',
        manuallyEdited: true,
        statutoryAmountOverride: true,
        sourceRating: 'operator_locked',
        sourceNote: 'Builder-approved statutory amount override. The deterministic default remains available for restore and review.',
        costBreakdown: {
            ...breakdown,
            otherCosts,
            finalExGstAddon: nextFinalExGst,
            finalIncGstAddon: nextFinalIncGst,
            pricingUnroundedIncGst: round2(amountIncGst),
            pricingRoundingDeltaIncGst: 0,
            statutoryAmountOverride: {
                generatedAmountIncGst: round2(money(generatedRow?.amount)),
                builderAmountIncGst: round2(amountIncGst),
                editedAt: priorRow?.amountEditedAt || priorRow?.amount_edited_at || null,
                editedBy: priorRow?.amountEditedBy || priorRow?.amount_edited_by || 'user',
            },
            _costSource: 'builder-statutory-amount-override',
        },
    };
}

function complianceMethodology(methodology = {}) {
    const configured = methodField(methodology, 'regulated_compliance', 'regulatedCompliance') || {};
    return {
        engineeringSrdDcdExGst: Math.max(0, Number(methodField(
            configured,
            'engineering_srd_dcd_ex_gst',
            'engineeringSrdDcdExGst',
        )) || 7700),
        dbpaRegistrationExGst: Math.max(0, Number(methodField(
            configured,
            'dbpa_registration_ex_gst',
            'dbpaRegistrationExGst',
        )) || 2500),
        fairTradingContractExGst: Math.max(0, Number(methodField(
            configured,
            'fair_trading_contract_ex_gst',
            'fairTradingContractExGst',
        )) || 1200),
    };
}

export function francisRegulatedComplianceBaseExGst(methodology = {}) {
    const compliance = complianceMethodology(methodology);
    return round2(compliance.engineeringSrdDcdExGst + compliance.dbpaRegistrationExGst);
}

function authoritativeWorkAmountExGst(item, gstRate) {
    const unroundedIncGst = money(item?.costBreakdown?.pricingUnroundedIncGst);
    const amountIncGst = unroundedIncGst > 0 ? unroundedIncGst : Math.max(0, money(item?.amount));
    return round2(amountIncGst / (1 + gstRate));
}

function complianceScopeRow({
    amountIncGst,
    unroundedIncGst,
    components,
    complianceStatus,
    complianceReason,
    methodologyId,
    premiumBaseExGst,
}) {
    const hasHbcf = components.some((component) => component.kind === 'hbcf_premium_estimate');
    return {
        id: FRANCIS_PRELIMINARIES_ID,
        code: 'PRELIMS',
        title: 'Preliminaries & Administration',
        description: hasHbcf
            ? 'Regulated design, NSW Planning Portal registration, contract preparation and HBCF insurance for the quoted building works.'
            : 'Regulated design and NSW Planning Portal registration for the quoted building works.',
        descriptionBrief: hasHbcf
            ? 'Regulated design, statutory registration, contract and HBCF requirements.'
            : 'Regulated design and statutory registration requirements.',
        inclusions: components
            .filter((component) => component.inclusion)
            .map((component) => component.inclusion),
        amount: String(round2(amountIncGst)),
        amountAuthority: 'committed',
        amountLocked: true,
        clientScopeItem: true,
        isAdminItem: true,
        adminKind: 'preliminaries_and_administration',
        systemManaged: FRANCIS_COMPLIANCE_SYSTEM_MANAGED,
        complianceStatus,
        complianceReason: String(complianceReason || '').trim() || null,
        excludeFromMethodCoherence: true,
        excludeFromFixedAllocations: true,
        gstExempt: false,
        taxTreatment: 'taxable_compliance_package',
        sourceRating: hasHbcf ? 'mixed_authority_and_estimate' : 'rate_book',
        sourceNote: hasHbcf
            ? 'Francis Skill rate-card authority. HBCF premium is a planning estimate pending the insurer certificate.'
            : 'Francis Skill rate-card authority.',
        sequence: 0,
        activityRefs: [],
        administrationGroupId: 'project',
        costBreakdown: {
            materials: [],
            labour: [],
            equipment: [],
            subcontractors: [],
            otherCosts: components.map((component) => ({
                description: component.label,
                qty: 1,
                unit: 'Per project',
                total: component.amountExGst,
                rateIsFinalChargeOut: true,
                rateTaxBasis: 'ex_gst',
                sourceRating: component.sourceRating || 'rate_book',
            })),
            directCostTotal: 0,
            contingencyPct: 0,
            contingencyAmount: 0,
            overheadPct: 0,
            overheadAmount: 0,
            totalCost: 0,
            marginPct: 0,
            marginAmount: 0,
            finalExGstAddon: round2(components.reduce((sum, component) => sum + component.amountExGst, 0)),
            finalIncGstAddon: 0,
            pricingMethodologyId: methodologyId,
            pricingUnroundedIncGst: round2(unroundedIncGst),
            pricingRoundingDeltaIncGst: round2(amountIncGst - unroundedIncGst),
            complianceComponents: components,
            premiumBaseExGst: round2(premiumBaseExGst),
            _costSource: 'francis-compliance-reconcile',
        },
    };
}

/**
 * Assemble Francis's regulated-work preliminaries as one deterministic client
 * row. Physical scope amounts already carry their equal project-admin shares;
 * this row therefore contains only SRD/DCD, DBPA and, when the strict
 * pre-HBCF total exceeds $20,000 inc GST, contract, application and premium.
 * A provisional Class 2 posture remains an explicit review hold, but does not
 * add SRD/DCD, DBPA or Portal fees that the supplied evidence has not yet
 * established. Ordinary HBCF still follows the strict pre-HBCF quote total.
 */
export function reconcileFrancisComplianceScope(scopeItems = [], {
    methodology = {},
    compliancePosture = { status: 'not_required' },
    hbcfWaived = false,
} = {}) {
    const input = Array.isArray(scopeItems) ? scopeItems : [];
    const priorComplianceRow = input.find(isManagedComplianceRow) || null;
    const workItems = input.filter((item) => !isManagedComplianceRow(item) && !isManagedHbcfRow(item));
    const postureStatus = String(compliancePosture?.status || 'not_required').trim().toLowerCase();
    if (postureStatus === 'provisional_required') {
        const hbcfOnly = reconcileFrancisHbcfScope(workItems, { methodology, hbcfWaived });
        return {
            ...hbcfOnly,
            status: `provisional_review_required_${hbcfOnly.status}`,
            blocked: true,
            complianceReviewReason: String(compliancePosture?.reason || '').trim() || null,
        };
    }
    if (postureStatus !== 'confirmed_required') {
        return {
            scopeItems: workItems,
            status: 'not_required',
            blocked: false,
            collisions: [],
            preHbcfQuoteTotalIncGst: round2(workItems.reduce((sum, item) => sum + Math.max(0, money(item?.amount)), 0)),
            totalIncGst: round2(workItems.reduce((sum, item) => sum + Math.max(0, money(item?.amount)), 0)),
        };
    }

    const collisions = workItems
        .filter((item) => COMPLIANCE_COLLISION_RE.test(`${item?.id || ''} ${item?.code || ''} ${item?.title || ''}`))
        .map((item) => String(item?.id || item?.code || item?.title || 'unknown'));
    if (collisions.length > 0) {
        return {
            scopeItems: input,
            status: 'collision',
            blocked: true,
            collisions,
            preHbcfQuoteTotalIncGst: 0,
            totalIncGst: round2(input.reduce((sum, item) => sum + Math.max(0, money(item?.amount)), 0)),
        };
    }

    const gstRate = Math.max(0, Number(methodField(methodology, 'gst_rate', 'gstRate')) || 0);
    const roundingUnit = Number(methodField(methodology, 'line_rounding_inc_gst', 'lineRoundingIncGst')) || 10;
    const hbcf = methodology?.hbcf || {};
    const thresholdIncGst = Math.max(0, Number(methodField(hbcf, 'threshold_inc_gst', 'thresholdIncGst')) || 0);
    const premiumRate = Math.max(0, Number(methodField(hbcf, 'premium_rate', 'premiumRate')) || 0);
    const applicationFeeExGst = Math.max(0, Number(methodField(hbcf, 'application_fee_ex_gst', 'applicationFeeExGst')) || 0);
    const compliance = complianceMethodology(methodology);
    const workBuildUpExGst = round2(workItems.reduce(
        (sum, item) => sum + authoritativeWorkAmountExGst(item, gstRate),
        0,
    ));
    const regulatedBaseExGst = round2(
        compliance.engineeringSrdDcdExGst + compliance.dbpaRegistrationExGst,
    );
    // HBCF eligibility belongs to the builder-priced works package. Do not let
    // QuoteMaker's own regulated-design, DBPA, contract, application or premium
    // additions bootstrap an otherwise under-$20,000 job over the threshold.
    // Those statutory/admin components remain visible quote costs when the
    // regulated-work posture requires them, but they are not eligibility work.
    const preHbcfQuoteTotalIncGst = calculateHbcfEligibilityBaseIncGst(workItems, {
        isGstInclusive: true,
        gstRate,
    });
    const hbcfRequired = hbcfWaived !== true && preHbcfQuoteTotalIncGst > thresholdIncGst;
    const premiumBaseExGst = round2(
        workBuildUpExGst
        + regulatedBaseExGst
        + (hbcfRequired ? compliance.fairTradingContractExGst + applicationFeeExGst : 0),
    );
    const premiumExGst = hbcfRequired ? round2(premiumBaseExGst * premiumRate) : 0;
    const components = [
        {
            kind: 'regulated_design',
            label: 'Engineering SRD and DCD',
            inclusion: 'Supply Engineering SRD and DCD',
            amountExGst: round2(compliance.engineeringSrdDcdExGst),
        },
        {
            kind: 'dbpa_registration',
            label: 'DBPA registration',
            inclusion: 'Supply NSW Planning Portal Registration (DBPA)',
            amountExGst: round2(compliance.dbpaRegistrationExGst),
        },
        ...(hbcfRequired ? [
            {
                kind: 'fair_trading_contract',
                label: 'NSW Fair Trading contract',
                inclusion: 'Supply NSW Fair Trading Home Building Contract',
                amountExGst: round2(compliance.fairTradingContractExGst),
            },
            {
                kind: 'hbcf_application',
                label: 'HBCF application',
                inclusion: 'Supply HBCF Insurance Certificate',
                amountExGst: round2(applicationFeeExGst),
            },
            {
                kind: 'hbcf_premium_estimate',
                label: 'HBCF premium estimate',
                inclusion: null,
                amountExGst: premiumExGst,
                sourceRating: 'estimate',
            },
        ] : []),
    ];
    const preliminariesExGst = round2(components.reduce((sum, component) => sum + component.amountExGst, 0));
    const preliminariesUnroundedIncGst = round2(preliminariesExGst * (1 + gstRate));
    // The constituent amounts remain the exact Stage 4 pricing floor. Nearest
    // clean-number rounding can therefore round a generated package below its
    // own authority (production Cremorne: $16,642.56 -> $16,640). Keep the
    // established nearest-$10 policy when it clears the build-up; otherwise
    // advance one unit so a generated quote never starts below known dollars.
    const preliminariesAmountIncGst = roundClientAmountIncGstAtOrAbove(
        preliminariesUnroundedIncGst,
        roundingUnit,
    );
    const generatedPreliminary = complianceScopeRow({
        amountIncGst: preliminariesAmountIncGst,
        unroundedIncGst: preliminariesUnroundedIncGst,
        components,
        complianceStatus: postureStatus,
        complianceReason: compliancePosture?.reason,
        methodologyId: String(methodology?.id || 'francis-sell-band-v1'),
        premiumBaseExGst,
    });
    // A change in HBCF disposition (included -> excluded or the reverse) must
    // rebuild from the deterministic component set. Otherwise preserve a
    // deliberate builder amount across reopen/re-cost/regeneration.
    const priorHbcfStateMatches = priorComplianceRow == null
        || managedScopeItemIncludesHbcf(priorComplianceRow) === hbcfRequired;
    const preliminary = priorHbcfStateMatches
        ? applyBuilderStatutoryAmountOverride(generatedPreliminary, priorComplianceRow, gstRate)
        : generatedPreliminary;
    const finalPreliminariesAmountIncGst = money(preliminary.amount);
    const totalIncGst = round2(
        finalPreliminariesAmountIncGst
        + workItems.reduce((sum, item) => sum + Math.max(0, money(item?.amount)), 0),
    );
    return {
        scopeItems: [preliminary, ...workItems],
        status: `${postureStatus}_${hbcfRequired ? 'hbcf' : 'no_hbcf'}`,
        blocked: postureStatus === 'provisional_required',
        collisions: [],
        preHbcfQuoteTotalIncGst,
        premiumBaseExGst,
        premiumExGst,
        totalClientAdditionIncGst: finalPreliminariesAmountIncGst,
        totalIncGst,
        hbcfRequired,
        builderAmountOverride: hasBuilderStatutoryAmountOverride(preliminary),
    };
}

export function assessFrancisComplianceScope(scopeItems = [], {
    methodology = {},
    hbcfWaived = false,
} = {}) {
    const input = Array.isArray(scopeItems) ? scopeItems : [];
    const managed = input.filter(isManagedComplianceRow);
    if (managed.length === 0) {
        return { ok: true, status: 'not_present', issues: [] };
    }
    const issues = [];
    const add = (code, message, detail = null) => issues.push({
        code,
        message,
        ...(detail == null ? {} : { detail }),
    });
    if (managed.length !== 1) {
        add('compliance-row-count-mismatch', 'The quote must contain exactly one system-managed Preliminaries & Administration row.');
    }
    const legacyHbcfRows = input.filter(isManagedHbcfRow);
    if (legacyHbcfRows.length > 0) {
        add(
            'compliance-hbcf-row-coexistence',
            'Separate system-managed HBCF rows cannot coexist with the combined Preliminaries & Administration package.',
            { ids: legacyHbcfRows.map(row => row?.id || row?.code || row?.title || 'unknown') },
        );
    }
    const actual = managed[0];
    const complianceStatus = String(actual?.complianceStatus || '').trim().toLowerCase();
    const expected = reconcileFrancisComplianceScope(input, {
        methodology,
        hbcfWaived,
        compliancePosture: {
            status: complianceStatus,
            reason: actual?.complianceReason,
        },
    });
    if (expected.status === 'collision') {
        add('manual-compliance-collision', 'A manual preliminaries or HBCF row conflicts with QuoteMaker\'s system-managed compliance package.', {
            collisions: expected.collisions,
        });
    } else {
        const expectedRow = expected.scopeItems.find(isManagedComplianceRow);
        if (!expectedRow || Math.abs(money(actual?.amount) - money(expectedRow?.amount)) > 0.01) {
            add('compliance-amount-mismatch', 'Preliminaries & Administration does not match the deterministic Francis rate-card amount.', {
                actualAmount: money(actual?.amount),
                expectedAmount: money(expectedRow?.amount),
            });
        }
    }
    if (complianceStatus === 'provisional_required') {
        add(
            'compliance-confirmation-required',
            actual?.complianceReason || 'Confirm the Class 2 regulated-work treatment with the appointed certifier before issue.',
        );
    } else if (complianceStatus !== 'confirmed_required') {
        add('compliance-status-invalid', 'The compliance package has no confirmed or provisional regulated-work status.');
    }
    if (actual?.amountAuthority !== 'committed'
        || actual?.clientScopeItem !== true
        || actual?.amountLocked !== true) {
        add(
            'compliance-authority-mismatch',
            'Preliminaries & Administration has lost its committed, locked client-visible authority.',
        );
    }
    return {
        ok: issues.length === 0,
        status: expected.status,
        preHbcfQuoteTotalIncGst: expected.preHbcfQuoteTotalIncGst,
        totalClientAdditionIncGst: expected.totalClientAdditionIncGst,
        builderAmountOverride: hasBuilderStatutoryAmountOverride(actual),
        issues,
    };
}

function hbcfScopeRow({
    id,
    code,
    title,
    description,
    inclusion,
    amountIncGst,
    finalExGstAddon = 0,
    finalIncGstAddon = 0,
    gstExempt,
    taxTreatment,
    methodologyId,
    sourceRating = 'rate_book',
    unroundedIncGst = amountIncGst,
}) {
    const roundingDelta = round2(amountIncGst - unroundedIncGst);
    return {
        id,
        code,
        title,
        description,
        descriptionBrief: description,
        inclusions: [inclusion],
        amount: String(round2(amountIncGst)),
        amountAuthority: 'committed',
        amountLocked: true,
        clientScopeItem: true,
        isAdminItem: true,
        adminKind: id === FRANCIS_HBCF_PREMIUM_ID ? 'hbcf_premium' : 'hbcf_application',
        systemManaged: FRANCIS_HBCF_SYSTEM_MANAGED,
        statutoryKind: id === FRANCIS_HBCF_PREMIUM_ID ? 'hbcf_premium_estimate' : 'hbcf_application_service',
        excludeFromMethodCoherence: true,
        excludeFromFixedAllocations: true,
        gstExempt: gstExempt === true,
        taxTreatment,
        sourceRating,
        sourceNote: sourceRating === 'estimate'
            ? 'Planning allowance only. Replace with the actual insurer premium before issue.'
            : 'Francis Skill rate-card authority.',
        sequence: 0,
        activityRefs: [],
        administrationGroupId: 'statutory-hbcf',
        costBreakdown: {
            materials: [],
            labour: [],
            equipment: [],
            subcontractors: [],
            otherCosts: [{
                description: title,
                qty: 1,
                unit: 'Per project',
                total: finalExGstAddon || finalIncGstAddon,
                rateIsFinalChargeOut: true,
                rateTaxBasis: finalExGstAddon > 0 ? 'ex_gst' : 'inc_gst',
                sourceRating,
            }],
            directCostTotal: 0,
            contingencyPct: 0,
            contingencyAmount: 0,
            overheadPct: 0,
            overheadAmount: 0,
            totalCost: 0,
            marginPct: 0,
            marginAmount: 0,
            finalExGstAddon: round2(finalExGstAddon),
            finalIncGstAddon: round2(finalIncGstAddon),
            pricingMethodologyId: methodologyId,
            pricingUnroundedIncGst: round2(unroundedIncGst),
            pricingRoundingDeltaIncGst: roundingDelta,
            statutoryKind: id === FRANCIS_HBCF_PREMIUM_ID ? 'hbcf_premium_estimate' : 'hbcf_application_service',
            _costSource: 'francis-statutory-reconcile',
        },
    };
}

/**
 * Reconcile the two visible HBCF rows after ordinary work has received Francis
 * Sell pricing. The threshold base always excludes HBCF itself. Re-running is
 * idempotent because only rows carrying the exact system-management identity
 * are removed and rebuilt; a manual/legacy collision fails closed for review.
 */
export function reconcileFrancisHbcfScope(scopeItems = [], {
    methodology = {},
    hbcfWaived = false,
} = {}) {
    const input = Array.isArray(scopeItems) ? scopeItems : [];
    const priorManagedRows = new Map(
        input.filter(isManagedHbcfRow).map((item) => [String(item?.id || ''), item]),
    );
    const workItems = input.filter((item) => !isManagedHbcfRow(item));
    const preHbcfQuoteTotalIncGst = calculateHbcfEligibilityBaseIncGst(workItems);
    const base = {
        scopeItems: workItems,
        preHbcfQuoteTotalIncGst,
        totalClientAdditionIncGst: 0,
        collisions: [],
        blocked: false,
    };

    if (hbcfWaived === true) return { ...base, status: 'waived' };

    const collisions = workItems
        .filter((item) => HBCF_TEXT_RE.test(`${item?.id || ''} ${item?.code || ''} ${item?.title || ''}`))
        .map((item) => String(item?.id || item?.code || item?.title || 'unknown'));
    if (collisions.length > 0) {
        return {
            ...base,
            scopeItems: input,
            status: 'collision',
            collisions,
            blocked: true,
        };
    }

    const projection = projectFrancisHbcf(preHbcfQuoteTotalIncGst, methodology);
    if (!projection.required) return { ...base, status: 'not_required' };

    const roundingUnit = Number(methodField(methodology, 'line_rounding_inc_gst', 'lineRoundingIncGst')) || 10;
    const premiumAmountIncGst = roundClientAmountIncGst(projection.premiumIncGst, roundingUnit);
    const applicationAmountIncGst = roundClientAmountIncGst(projection.applicationFeeIncGst, roundingUnit);
    const methodologyId = String(methodology?.id || 'francis-sell-band-v1');
    const generatedPremium = hbcfScopeRow({
        id: FRANCIS_HBCF_PREMIUM_ID,
        code: 'HBCF',
        title: 'HBCF Premium (Estimated)',
        description: 'Estimated Home Building Compensation Fund premium for the residential building works included in this quote.',
        inclusion: 'Provide the HBCF insurance certificate before taking payment or commencing the covered works.',
        amountIncGst: premiumAmountIncGst,
        finalIncGstAddon: premiumAmountIncGst,
        gstExempt: true,
        taxTreatment: 'statutory_premium_estimate',
        methodologyId,
        sourceRating: 'estimate',
        unroundedIncGst: projection.premiumIncGst,
    });
    const generatedApplication = hbcfScopeRow({
        id: FRANCIS_HBCF_APPLICATION_ID,
        code: 'HBCF-APP',
        title: 'HBCF Application Service',
        description: 'Prepare, lodge and administer the Home Building Compensation Fund application for the quoted works.',
        inclusion: 'HBCF application administration and certificate coordination.',
        amountIncGst: applicationAmountIncGst,
        finalExGstAddon: projection.applicationFeeExGst,
        gstExempt: false,
        taxTreatment: 'taxable_service',
        methodologyId,
        unroundedIncGst: projection.applicationFeeIncGst,
    });
    const gstRate = Math.max(0, Number(methodField(methodology, 'gst_rate', 'gstRate')) || 0.1);
    const premium = applyBuilderStatutoryAmountOverride(
        generatedPremium,
        priorManagedRows.get(FRANCIS_HBCF_PREMIUM_ID),
        gstRate,
    );
    const application = applyBuilderStatutoryAmountOverride(
        generatedApplication,
        priorManagedRows.get(FRANCIS_HBCF_APPLICATION_ID),
        gstRate,
    );
    const startSequence = workItems.reduce((max, item) => Math.max(max, Number(item?.sequence) || 0), 0);
    premium.sequence = startSequence + 1;
    application.sequence = startSequence + 2;
    const totalClientAdditionIncGst = round2(money(premium.amount) + money(application.amount));
    return {
        ...base,
        scopeItems: [...workItems, premium, application],
        status: 'estimated_required',
        projection,
        totalClientAdditionIncGst,
        builderAmountOverride: hasBuilderStatutoryAmountOverride(premium)
            || hasBuilderStatutoryAmountOverride(application),
    };
}

/**
 * Reconcile the builder's statutory quote rows through the correct shape.
 * Regulated projects carry HBCF inside the combined Preliminaries row; other
 * projects use the two standalone HBCF rows. UI decision controls use this
 * single seam so excluding/restoring HBCF can never accidentally delete the
 * regulated-design and DBPA components that share the combined row.
 */
export function reconcileFrancisStatutoryScope(scopeItems = [], {
    methodology = {},
    compliancePosture = null,
    hbcfWaived = false,
} = {}) {
    const input = Array.isArray(scopeItems) ? scopeItems : [];
    const persistedComplianceRow = input.find(isManagedComplianceRow) || null;
    // A persisted managed compliance package is the durable commercial shape
    // of this quote. Reopened projects can temporarily expose an older or
    // incomplete WMS posture; that must not route a combined SRD/DBPA/HBCF row
    // through the standalone-HBCF reconciler and falsely certify exclusion
    // while leaving the premium inside the combined row.
    const persistedStatus = String(
        persistedComplianceRow?.complianceStatus
        || persistedComplianceRow?.compliance_status
        || '',
    ).trim().toLowerCase();
    const resolvedPosture = persistedComplianceRow ? {
        status: persistedStatus === 'provisional_required'
            ? 'provisional_required'
            : 'confirmed_required',
        reason: persistedComplianceRow.complianceReason
            || persistedComplianceRow.compliance_reason
            || compliancePosture?.reason,
    } : compliancePosture;
    const complianceStatus = String(resolvedPosture?.status || '').trim().toLowerCase();
    if (complianceStatus === 'confirmed_required' || complianceStatus === 'provisional_required') {
        return reconcileFrancisComplianceScope(input, {
            methodology,
            compliancePosture: resolvedPosture,
            hbcfWaived,
        });
    }
    return reconcileFrancisHbcfScope(input, { methodology, hbcfWaived });
}

/**
 * Audit the persisted/client-visible HBCF rows against the deterministic
 * Francis projection. This is deliberately pure and side-effect free so the
 * Stage 5 issue gate can fail closed without regenerating or mutating scope.
 */
export function assessFrancisHbcfScope(scopeItems = [], {
    methodology = {},
    hbcfWaived = false,
} = {}) {
    const input = Array.isArray(scopeItems) ? scopeItems : [];
    if (input.some(isManagedComplianceRow)) {
        return assessFrancisComplianceScope(input, { methodology, hbcfWaived });
    }
    const managedRows = input.filter(isManagedHbcfRow);
    const expected = reconcileFrancisHbcfScope(input, { methodology, hbcfWaived });
    const issues = [];
    const add = (code, message, detail = null) => issues.push({
        code,
        message,
        ...(detail == null ? {} : { detail }),
    });

    if (expected.status === 'collision') {
        add(
            'manual-hbcf-collision',
            'A manual or legacy HBCF row conflicts with QuoteMaker\'s system-managed statutory rows.',
            { collisions: expected.collisions },
        );
    }

    if (expected.status === 'waived') {
        if (managedRows.length > 0) {
            add('hbcf-present-while-waived', 'HBCF is waived but system-managed HBCF rows remain in the quote.');
        }
    } else if (expected.status === 'not_required') {
        if (managedRows.length > 0) {
            add('hbcf-present-below-threshold', 'System-managed HBCF rows are present even though the pre-HBCF quote is not over $20,000 inc GST.');
        }
    } else if (expected.status === 'estimated_required') {
        const expectedRows = expected.scopeItems.filter(isManagedHbcfRow);
        for (const expectedRow of expectedRows) {
            const matches = managedRows.filter((row) => row?.id === expectedRow.id);
            if (matches.length !== 1) {
                add(
                    matches.length === 0 ? 'hbcf-row-missing' : 'hbcf-row-duplicate',
                    `${expectedRow.title} must appear exactly once.`,
                    { id: expectedRow.id, count: matches.length },
                );
                continue;
            }
            const actual = matches[0];
            const actualAmount = money(actual?.amount);
            const expectedAmount = money(expectedRow?.amount);
            if (Math.abs(actualAmount - expectedAmount) > 0.01) {
                add('hbcf-amount-mismatch', `${expectedRow.title} does not match the deterministic amount.`, {
                    id: expectedRow.id,
                    actualAmount,
                    expectedAmount,
                });
            }
            if (actual?.amountAuthority !== 'committed'
                || actual?.clientScopeItem !== true
                || actual?.amountLocked !== true) {
                add('hbcf-authority-mismatch', `${expectedRow.title} has lost its committed client-visible authority.`, { id: expectedRow.id });
            }
            if (expectedRow.id === FRANCIS_HBCF_PREMIUM_ID && actual?.gstExempt !== true) {
                add('hbcf-premium-tax-mismatch', 'The estimated HBCF premium must not receive GST a second time.');
            }
            if (expectedRow.id === FRANCIS_HBCF_APPLICATION_ID
                && String(actual?.taxTreatment || '').toLowerCase() !== 'taxable_service') {
                add('hbcf-application-tax-mismatch', 'The HBCF application service must remain a taxable service.');
            }
        }
        if (managedRows.length !== expectedRows.length) {
            add('hbcf-row-count-mismatch', 'The quote does not contain exactly the two required system-managed HBCF rows.', {
                actual: managedRows.length,
                expected: expectedRows.length,
            });
        }
    }

    return {
        ok: issues.length === 0,
        status: expected.status,
        preHbcfQuoteTotalIncGst: expected.preHbcfQuoteTotalIncGst,
        totalClientAdditionIncGst: expected.totalClientAdditionIncGst,
        builderAmountOverride: managedRows.some(hasBuilderStatutoryAmountOverride),
        issues,
    };
}

function feeForTotal(totalIncGst, tiers = []) {
    const sorted = [...tiers].sort((left, right) => {
        const leftMax = methodField(left, 'max_total_inc_gst', 'maxTotalIncGst');
        const rightMax = methodField(right, 'max_total_inc_gst', 'maxTotalIncGst');
        const a = leftMax == null ? Infinity : Number(leftMax);
        const b = rightMax == null ? Infinity : Number(rightMax);
        return a - b;
    });
    for (const tier of sorted) {
        const rawMax = methodField(tier, 'max_total_inc_gst', 'maxTotalIncGst');
        const max = rawMax == null ? Infinity : Number(rawMax);
        if (totalIncGst <= max) {
            return Math.max(0, Number(methodField(tier, 'amount_ex_gst', 'amountExGst')) || 0);
        }
    }
    return 0;
}

/**
 * Resolve the project administration tier against the total that includes the
 * fee itself. The tier schedule is stepped, so a bounded fixed-point iteration
 * is deterministic and exposes threshold crossings rather than hiding them.
 */
export function resolveAdministrationFeeExGst(
    baseTotalIncGst,
    methodology = {},
    bandMultiplier = 1,
) {
    const gstRate = Math.max(0, Number(methodField(methodology, 'gst_rate', 'gstRate')) || 0);
    const band = Math.max(0, Number(bandMultiplier) || 1);
    const configuredTiers = methodField(methodology, 'administration_fee_ex_gst_tiers', 'administrationFeeExGstTiers');
    const tiers = Array.isArray(configuredTiers)
        ? configuredTiers
        : [];
    let fee = feeForTotal(Number(baseTotalIncGst) || 0, tiers);
    for (let pass = 0; pass < 8; pass += 1) {
        const totalWithFee = (Number(baseTotalIncGst) || 0) + fee * band * (1 + gstRate);
        const next = feeForTotal(totalWithFee, tiers);
        if (next === fee) return round2(fee);
        fee = next;
    }
    return round2(fee);
}

/**
 * `administrationGroupId` is an approval/dependency identity, not a monetary
 * value. Dependent rows share one id and therefore one tiered fee. A scope row
 * that the client may accept independently receives its own id and fee. When
 * no identity is supplied, all rows conservatively remain one project group.
 *
 * @param {Array<{id:string, administrationGroupId?:string, finalExGst:number, finalIncGst:number, markedUpIncGst:number, eligible?:boolean}>} entries
 * @returns {{items:Array, band:Object, administrationFeeExGst:number, administrationGroups:Array, totalIncGst:number}}
 */
export function priceAuthoritativeRateCardStreams(entries = [], {
    methodology = {},
    band = null,
    projectAdministrationBaseExGst = 0,
} = {}) {
    const gstRate = Math.max(0, Number(methodField(methodology, 'gst_rate', 'gstRate')) || 0);
    const resolvedBand = normalisePricingBand(band, methodology);
    const prepared = entries.map((entry, index) => {
        const finalExGst = Math.max(0, Number(entry?.finalExGst) || 0);
        const finalIncGst = Math.max(0, Number(entry?.finalIncGst) || 0);
        const markedUpIncGst = Math.max(0, Number(entry?.markedUpIncGst) || 0);
        const bandedFinalExGst = round2(finalExGst * resolvedBand.multiplier);
        // Francis applies the band LAST to the whole ex-GST build-up. Convert
        // any legacy/missing-rate stream back to ex GST before banding; in the
        // strict authority path finalExGst is ordinarily the only non-zero one.
        const unbandedBaseExGst = round2(
            finalExGst
            + (finalIncGst + markedUpIncGst) / (1 + gstRate),
        );
        const bandedBaseExGst = round2(unbandedBaseExGst * resolvedBand.multiplier);
        const baseIncGst = round2(bandedBaseExGst * (1 + gstRate));
        return {
            id: String(entry?.id || index),
            administrationGroupId: String(entry?.administrationGroupId || 'project').trim() || 'project',
            eligible: entry?.eligible !== false && baseIncGst > 0,
            finalExGst: round2(finalExGst),
            finalIncGst: round2(finalIncGst),
            markedUpIncGst: round2(markedUpIncGst),
            unbandedBaseExGst,
            bandedBaseExGst,
            bandedFinalExGst,
            baseIncGst,
        };
    });
    const groupEntries = new Map();
    for (const entry of prepared) {
        if (!entry.eligible) continue;
        const rows = groupEntries.get(entry.administrationGroupId) || [];
        rows.push(entry);
        groupEntries.set(entry.administrationGroupId, rows);
    }
    const extraProjectBaseExGst = Math.max(0, Number(projectAdministrationBaseExGst) || 0);
    const administrationGroups = extraProjectBaseExGst > 0
        ? (() => {
            const rows = prepared.filter((entry) => entry.eligible);
            const physicalBaseIncGst = round2(rows.reduce((sum, entry) => sum + entry.baseIncGst, 0));
            const baseTotalIncGst = round2(
                physicalBaseIncGst
                + extraProjectBaseExGst * resolvedBand.multiplier * (1 + gstRate),
            );
            const feeExGst = resolveAdministrationFeeExGst(
                baseTotalIncGst,
                methodology,
                resolvedBand.multiplier,
            );
            return [{
                id: 'project',
                eligibleItemCount: rows.length,
                baseTotalIncGst,
                feeExGst,
                shareExGst: rows.length > 0 ? feeExGst / rows.length : 0,
                additionalComplianceBaseExGst: extraProjectBaseExGst,
            }];
        })()
        : [...groupEntries.entries()].map(([id, rows]) => {
        const baseTotalIncGst = round2(rows.reduce((sum, entry) => sum + entry.baseIncGst, 0));
        const feeExGst = resolveAdministrationFeeExGst(
            baseTotalIncGst,
            methodology,
            resolvedBand.multiplier,
        );
        return {
            id,
            eligibleItemCount: rows.length,
            baseTotalIncGst,
            feeExGst,
            shareExGst: rows.length > 0 ? feeExGst / rows.length : 0,
        };
        });
    const groupById = new Map(administrationGroups.map((group) => [group.id, group]));
    const administrationFeeExGst = round2(
        administrationGroups.reduce((sum, group) => sum + group.feeExGst, 0),
    );
    const roundingUnit = Number(methodField(methodology, 'line_rounding_inc_gst', 'lineRoundingIncGst')) || 10;

    let items = prepared.map((entry) => {
        const group = extraProjectBaseExGst > 0
            ? groupById.get('project')
            : groupById.get(entry.administrationGroupId);
        const adminShareExGst = entry.eligible ? Number(group?.shareExGst) || 0 : 0;
        const bandedAdministrationShareExGst = round2(adminShareExGst * resolvedBand.multiplier);
        const unroundedIncGst = round2(entry.baseIncGst + bandedAdministrationShareExGst * (1 + gstRate));
        const amountIncGst = roundClientAmountIncGst(unroundedIncGst, roundingUnit);
        return {
            ...entry,
            ...(extraProjectBaseExGst > 0 ? { administrationGroupId: 'project' } : {}),
            administrationShareExGst: round2(adminShareExGst),
            bandedAdministrationShareExGst,
            unroundedIncGst,
            amountIncGst,
            gstIncluded: round2(amountIncGst - amountIncGst / (1 + gstRate)),
            minimumAttendanceAllocationIncGst: 0,
        };
    });

    // Francis treats mobilisation as a real group-level floor: a separately
    // acceptable small job still consumes travel, setup, protection and close-
    // down. Enforce the documented lower edge ($1,200) per approval group, not
    // per dependent scope row. Allocate any shortfall to the first eligible row
    // so item totals and the project total remain exactly reconcilable.
    const configuredMinimumAttendance = methodField(
        methodology,
        'minimum_attendance_range_inc_gst',
        'minimumAttendanceRangeIncGst',
    );
    const minimumAttendanceRange = Array.isArray(configuredMinimumAttendance)
        ? configuredMinimumAttendance
        : [];
    const minimumAttendanceIncGst = Math.max(0, Number(minimumAttendanceRange[0]) || 0);
    if (minimumAttendanceIncGst > 0) {
        items = items.map((item) => ({ ...item }));
        for (const group of administrationGroups) {
            const groupIndexes = items
                .map((item, index) => ({ item, index }))
                .filter(({ item }) => item.eligible && item.administrationGroupId === group.id)
                .map(({ index }) => index);
            if (groupIndexes.length === 0) continue;
            const groupTotal = round2(groupIndexes.reduce((sum, index) => sum + items[index].amountIncGst, 0));
            if (groupTotal >= minimumAttendanceIncGst) continue;
            const shortfall = roundClientAmountIncGst(minimumAttendanceIncGst - groupTotal, roundingUnit);
            const targetIndex = groupIndexes[0];
            const target = items[targetIndex];
            const amountIncGst = round2(target.amountIncGst + shortfall);
            items[targetIndex] = {
                ...target,
                amountIncGst,
                minimumAttendanceAllocationIncGst: shortfall,
                gstIncluded: round2(amountIncGst - amountIncGst / (1 + gstRate)),
            };
        }
    }

    return {
        items,
        band: resolvedBand,
        administrationFeeExGst,
        administrationGroups,
        administrationAllocation: 'one_tier_per_approval_group_equal_within_group',
        minimumAttendanceIncGst,
        totalIncGst: round2(items.reduce((sum, item) => sum + item.amountIncGst, 0)),
    };
}
